/**
 * Read dropped files from DataTransfer, preserving folder structure when supported.
 * @returns {Promise<{ files: File[], relativePaths: string[] }>}
 */

async function readFileFromEntry(entry) {
  if (!entry) return null;
  if (entry instanceof File) return entry;
  if (entry.file instanceof File || entry.file instanceof Blob) {
    return entry.file;
  }
  if (typeof entry.file === 'function') {
    return await new Promise((resolve, reject) => {
      entry.file(resolve, reject);
    });
  }
  return null;
}

function dataTransferHasDirectory(dataTransfer) {
  const items = dataTransfer?.items;
  if (!items?.length) return false;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.() || item.getAsEntry?.();
    if (entry?.isDirectory) return true;
  }
  return false;
}

/**
 * @returns {Promise<{ files: File[], relativePaths: string[] }>}
 */
export async function collectFilesFromDrop(dataTransfer) {
  const nativeFiles = dataTransfer?.files ? [...dataTransfer.files] : [];

  if (nativeFiles.length && !dataTransferHasDirectory(dataTransfer)) {
    return { files: nativeFiles, relativePaths: nativeFiles.map((f) => f.name) };
  }

  const items = dataTransfer?.items;
  if (!items?.length) {
    return { files: nativeFiles, relativePaths: nativeFiles.map((f) => f.name) };
  }

  const files = [];
  const relativePaths = [];

  async function walk(entry, prefix) {
    if (entry.isDirectory) {
      const reader = entry.createReader();
      const readBatch = () =>
        new Promise((resolve, reject) => {
          reader.readEntries(resolve, reject);
        });
      let batch = await readBatch();
      while (batch.length) {
        for (const child of batch) {
          await walk(child, prefix ? `${prefix}/${entry.name}` : entry.name);
        }
        batch = await readBatch();
      }
      return;
    }

    const file = await readFileFromEntry(entry);
    if (!file) return;
    const rel = prefix ? `${prefix}/${file.name}` : file.name;
    files.push(file);
    relativePaths.push(rel);
  }

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.() || item.getAsEntry?.();
    if (entry) {
      await walk(entry, '');
      continue;
    }
    const file = item.getAsFile();
    if (file) {
      files.push(file);
      relativePaths.push(file.name);
    }
  }

  if (!files.length && nativeFiles.length) {
    return { files: nativeFiles, relativePaths: nativeFiles.map((f) => f.name) };
  }

  return { files, relativePaths };
}
